import { AfterViewInit, ContentChildren, Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, Renderer2, SimpleChanges } from '@angular/core';
import { ReplaySubject, Subject, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { hasMany } from '@skylab-shared/utils';

import { ColumnDef, ColumnWidth } from '../grid-ins.component';
import { GridCellResizeDirective } from './grid-cell-resize.directive';
import { GridResizeEvent } from './grid-resize.model';
import { GridResizeService } from './grid-resize.service';
import { HeaderResizerComponent } from './header-resizer.component';

export type GridResizedEvent<TEntity, TCustomField = never> = { columnDef: ColumnDef<TEntity, TCustomField>, columnWidth: ColumnWidth };
export type GridResizingEvent<TEntity, TCustomField = never> = GridResizedEvent<TEntity, TCustomField>;
export interface ColumnSizes<TEntity> {
    readonly width?: ColumnWidth | '*';
    readonly minWidth?: ColumnWidth;
    readonly fillingColumn: boolean;
    readonly columnDef: ColumnDef<TEntity>;
}

export type GridResizeEventData<TEntity, TCustomField> = { width: number, columnDef: ColumnDef<TEntity, TCustomField> };

@Directive({ selector: '[sklGridResizable]' })
export class GridResizableDirective<TEntity, TCustomField = never> implements AfterViewInit, OnChanges, OnDestroy {

    @ContentChildren(HeaderResizerComponent, { descendants: true }) protected set headerResizer(headers: QueryList<HeaderResizerComponent<TEntity, TCustomField>>) {
        if (headers.toArray().length > 0) {
             this.headers$.next(headers.toArray());
        }
    }

    @ContentChildren(GridCellResizeDirective) protected set cellResize(cells: QueryList<GridCellResizeDirective<TEntity, TCustomField>>) {
        if (cells.toArray().length > 0) {
            this.cells$.next(cells.toArray());
        }
    }

    @Input('sklGridResizableData') public columnSizes!: ColumnSizes<TEntity>[];

    @Output() public sklGridResizableOnResized = new EventEmitter<GridResizedEvent<TEntity, TCustomField>>();

    @Output() public sklGridResizableOnResizing = new EventEmitter<GridResizingEvent<TEntity, TCustomField>>();

    private fillingColumnName!: string;
    private fillingColumnTouched = false;
    private columnSizesChange$: ReplaySubject<ColumnSizes<TEntity>[]> = new ReplaySubject(1);
    private headerResizing!: Subscription;
    private headerResizied!: Subscription;
    private headerResizerTouched!: Subscription;
    private headerSizesChangeSubscription!: Subscription;
    private cellSizesChangeSubscription!: Subscription;
    private headers$ = new Subject<HeaderResizerComponent<TEntity, TCustomField>[]>();
    private cells$ = new Subject<GridCellResizeDirective<TEntity, TCustomField>[]>();
    private destroy$ = new Subject<void>();

    public constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef<HTMLElement>,
        private gridResize: GridResizeService
    ) { }

    public ngAfterViewInit(): void {
        const headersSizesChange$ = combineLatest([this.headers$, this.columnSizesChange$]).pipe(distinctUntilChanged(), takeUntil(this.destroy$));
        const cellSizesChange$ = combineLatest([this.cells$, this.columnSizesChange$]).pipe(distinctUntilChanged(), takeUntil(this.destroy$));

        this.headerSizesChangeSubscription = headersSizesChange$.subscribe(([headers, columnSizes]) => this.onHeadersSizesChange(headers, columnSizes));
        this.cellSizesChangeSubscription = cellSizesChange$.subscribe(([cells, columnSizes]) => this.onCellsSizesChange(cells, columnSizes));

        this.renderer.setStyle(this.elementRef.nativeElement, 'width', '100%', 2);
        this.headerResizing = this.gridResize.on('HeaderResizing', this.elementRef.nativeElement, (event: GridResizeEventData<TEntity, TCustomField>) => this.onHeaderResizing(event));
        this.headerResizied = this.gridResize.on('HeaderResizied', this.elementRef.nativeElement, (event: GridResizeEventData<TEntity, TCustomField>) => this.onHeaderResized(event));
        this.headerResizerTouched = this.gridResize.on('HeaderResizerTouched', this.elementRef.nativeElement, (event: boolean) => this.onHeaderResizerTouched(event));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['columnSizes']) {
            this.columnSizesChange$.next(changes['columnSizes'].currentValue);
        }
    }

    public ngOnDestroy(): void {
        this.columnSizesChange$.complete();
        this.headerSizesChangeSubscription?.unsubscribe?.();
        this.cellSizesChangeSubscription?.unsubscribe?.();
        this.headerResizing?.unsubscribe?.();
        this.headerResizied?.unsubscribe?.();
        this.headerResizerTouched?.unsubscribe?.();
        this.destroy$.next();
    }

    private get fillingColumnElement(): HTMLElement | null {
        const name = this.fillingColumnName?.toLowerCase();
        if (!name) return null;

        return this.elementRef.nativeElement.querySelector(`[id*='${name}']`);
    }

    private handleResizing(columnDef: ColumnDef<TEntity, TCustomField>, width: number): void {
        this.sklGridResizableOnResizing.emit({ columnDef, columnWidth: `${ width }px`as ColumnWidth });
    }

    private handleResizied(columnDef: ColumnDef<TEntity, TCustomField>, width: number): void {
        this.sklGridResizableOnResized.emit({ columnDef, columnWidth: `${ width }px`as ColumnWidth });
    }

    private onHeaderResized(event: GridResizeEventData<TEntity, TCustomField>): void {
        this.handleResizied(event.columnDef, event.width);
        this.renderer.removeStyle(this.elementRef.nativeElement, '--filling-column-width', 2);
    }

    private onHeaderResizerTouched(isTouched: boolean): void {
        if (!this.fillingColumnTouched) {
            this.gridResize.emit(new GridResizeEvent('FillingColumnTouched', isTouched, this.elementRef.nativeElement));
        }
        this.fillingColumnTouched = isTouched;
    }

    private onHeaderResizing(event: GridResizeEventData<TEntity, TCustomField>): void {
        this.handleResizing(event.columnDef, event.width);

        if (this.fillingColumnElement) {
            this.renderer.setStyle(this.elementRef.nativeElement, '--filling-column-width', `${ this.fillingColumnElement.offsetWidth ?? 0 }px`, 2);
        }
    }

    private onHeadersSizesChange(headers: HeaderResizerComponent<TEntity, TCustomField>[], columnSizes: ColumnSizes<TEntity>[]): void {
        if (hasMany(columnSizes, column => column.fillingColumn)) {
            throw new Error('The fillingColumn property can be used only once in columns configuration');
        }

        headers.forEach(header => {
            header.initHeaderSize(columnSizes);
        });

        this.fillingColumnName = (columnSizes.find(column => column.fillingColumn)?.columnDef as string);
    }

    private onCellsSizesChange(cells: GridCellResizeDirective<TEntity, TCustomField>[], columnSizes: ColumnSizes<TEntity>[]): void {
        cells.forEach(cell => {
            cell.initCellSize(columnSizes);
        });
    }
}
